import React, { useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import VariantBuilderPanelTitle from "../variants/VariantBuilderPanelTitle";
import TabGroup from "../shared/TabGroup";
import { flexSiblingWidthClearUpdates } from "../../utils/builderUtils";
import { getPanelParentBackLink } from "../../utils/panelNavigationUtils";
import useCodeIndicators from "../../hooks/useCodeIndicators";

const SectionColumnSettings = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const section_column = props.section_column;
  const structure = section_column.options || {};
  const codeIndicators = useCodeIndicators();
  const variant = contextData.objects.variant;
  const section =
    variant.sections && variant.sections[section_column.section_id];

  const tabs = ["Layout", "Design"];
  const tab = tabs.includes(contextData.selected_object_tab)
    ? contextData.selected_object_tab
    : "Layout";
  const showLayout = tab === "Layout";
  const showDesign = tab === "Design";

  const toggleWidthLock = (widthKey) => () => {
    builder.update([
      {
        object_type: "section_columns",
        object_id: section_column.id,
        setting_name: `[options][${widthKey}_locked]`,
        value: structure[`${widthKey}_locked`] ? "" : "true",
      },
    ]);
  };

  const flexibleWidthAdjust = (widthKey) => () => {
    if (!section) return;

    const updates = flexSiblingWidthClearUpdates(
      section,
      variant,
      section_column.id,
      widthKey
    );
    if (updates.length) builder.update(updates);
  };

  const customClassesSetting = (
    <VariantBuilderSetting
      object={section_column}
      setting_name="[options][custom-class]"
      label="Custom Classes"
      indicators={codeIndicators.get("section_columns", section_column.id, {
        type: "custom_class",
      })}
      placeholder="Custom class names here"
      setting_type="text"
      object_type="section_columns"
      object_id={section_column.id}
      value={structure["custom-class"]}
      alphanumeric={true}
      update_on_input_end={true}
    />
  );

  const layoutSettings = (
    <React.Fragment>
      <VariantBuilderSetting
        object={section_column}
        setting_name="[options][width]"
        setting_type="range"
        units={["px", "%"]}
        label="Column Width"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["width"] || ""}
        min={0}
        max={2560}
        range_step="1"
        placeholder="Auto"
        allow_empty={true}
        show_reset={true}
        condition={contextData.device == "desktop"}
        lockable={true}
        locked={!!structure["width_locked"]}
        lockTitle="Lock column width"
        unlockTitle="Unlock column width"
        onToggleLock={toggleWidthLock("width")}
        callback={flexibleWidthAdjust("width")}
        tooltip="Set this column's width as px or % (e.g. 400px or 50%; a plain number is px). Leave empty (Auto) to use the columns-per-row default. Column widths in a row should sum to the row width before columns wrap."
      />

      <VariantBuilderSetting
        object={section_column}
        setting_name="[options][width-mobile]"
        setting_type="range"
        units={["px", "%"]}
        label="Column Width"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["width-mobile"] || ""}
        min={0}
        max={2560}
        range_step="1"
        placeholder="Auto"
        allow_empty={true}
        show_reset={true}
        condition={contextData.device == "mobile"}
        lockable={true}
        locked={!!structure["width-mobile_locked"]}
        lockTitle="Lock column width"
        unlockTitle="Unlock column width"
        onToggleLock={toggleWidthLock("width-mobile")}
        callback={flexibleWidthAdjust("width-mobile")}
        tooltip="Set this column's width on mobile as px or %. Leave empty (Auto) to inherit the desktop width or the columns-per-row default."
      />

      <VariantBuilderSetting
        object={section_column}
        setting_name="[options][SPACING_TYPE-"
        setting_type="spacing"
        object_type="section_columns"
        object_id={section_column.id}
        label="Column Padding"
        condition={contextData.device == "desktop"}
        note="Click to increase and reduce padding"
        padding_only={true}
      >
        <div className="row hard-center" style={{ marginTop: "15px" }}>
          <div className="col-xs-12">
            <div className="mb-[5px] text-xl text-black dark:text-white">
              Inner padding
            </div>
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-left]"
              setting_type="text"
              object_type="section_columns"
              object_id={section_column.id}
              value={builder.helpers.convertLegacyPadding(
                structure["padding-left"]
              )}
              numeric_with_percentage={true}
              placeholder="0px"
              col="3"
              note="Left"
              classNames="spacing-inputs"
              allow_empty={true}
            />
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-top]"
              setting_type="text"
              object_type="section_columns"
              object_id={section_column.id}
              value={builder.helpers.convertLegacyPadding(
                structure["padding-top"]
              )}
              numeric_with_percentage={true}
              placeholder="0px"
              col="3"
              note="Top"
              classNames="spacing-inputs"
              allow_empty={true}
            />
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-bottom]"
              setting_type="text"
              object_type="section_columns"
              object_id={section_column.id}
              value={builder.helpers.convertLegacyPadding(
                structure["padding-bottom"]
              )}
              numeric_with_percentage={true}
              placeholder="0px"
              col="3"
              note="Bottom"
              classNames="spacing-inputs"
              allow_empty={true}
            />
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-right]"
              setting_type="text"
              object_type="section_columns"
              object_id={section_column.id}
              value={builder.helpers.convertLegacyPadding(
                structure["padding-right"]
              )}
              numeric_with_percentage={true}
              placeholder="0px"
              col="3"
              note="Right"
              classNames="spacing-inputs"
              allow_empty={true}
            />
          </div>
        </div>
      </VariantBuilderSetting>

      <VariantBuilderSetting
        object={section_column}
        setting_name="[options][SPACING_TYPE-mobile-"
        setting_type="spacing"
        object_type="section_columns"
        object_id={section_column.id}
        label="Column Padding"
        condition={contextData.device == "mobile"}
        device="mobile"
        note="Click to increase and reduce padding"
        padding_only={true}
      >
        <div className="row hard-center" style={{ marginTop: "15px" }}>
          <div className="col-xs-12">
            <div className="mb-[5px] text-xl text-black dark:text-white">
              Inner padding
            </div>
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-mobile-left]"
              setting_type="text"
              numeric_with_percentage={true}
              object_type="section_columns"
              object_id={section_column.id}
              value={
                builder.helpers.convertLegacyPadding(
                  structure["padding-mobile-left"]
                ) ||
                builder.helpers.convertLegacyPadding(structure["padding-left"])
              }
              placeholder="0px"
              col="3"
              note="Left"
              classNames="spacing-inputs"
            />
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-mobile-top]"
              setting_type="text"
              numeric_with_percentage={true}
              object_type="section_columns"
              object_id={section_column.id}
              value={
                builder.helpers.convertLegacyPadding(
                  structure["padding-mobile-top"]
                ) ||
                builder.helpers.convertLegacyPadding(structure["padding-top"])
              }
              placeholder="0px"
              col="3"
              note="Top"
              classNames="spacing-inputs"
            />
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-mobile-bottom]"
              setting_type="text"
              numeric_with_percentage={true}
              object_type="section_columns"
              object_id={section_column.id}
              value={
                builder.helpers.convertLegacyPadding(
                  structure["padding-mobile-bottom"]
                ) ||
                builder.helpers.convertLegacyPadding(
                  structure["padding-bottom"]
                )
              }
              placeholder="0px"
              col="3"
              note="Bottom"
              classNames="spacing-inputs"
            />
            <VariantBuilderSetting
              object={section_column}
              setting_name="[options][padding-mobile-right]"
              setting_type="text"
              numeric_with_percentage={true}
              object_type="section_columns"
              object_id={section_column.id}
              value={
                builder.helpers.convertLegacyPadding(
                  structure["padding-mobile-right"]
                ) ||
                builder.helpers.convertLegacyPadding(structure["padding-right"])
              }
              placeholder="0px"
              col="3"
              note="Right"
              classNames="spacing-inputs"
            />
          </div>
        </div>
      </VariantBuilderSetting>

      <VariantBuilderSetting
        object={section_column}
        setting_name="[options][hide-mobile]"
        setting_type="checkbox"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["hide-mobile"] ? structure["hide-mobile"] : "false"}
        label="Hide on mobile"
        tooltip="Want to display this only on desktop? Enable this option"
      />

      <VariantBuilderSetting
        object={section_column}
        setting_name="[options][hide-desktop]"
        setting_type="checkbox"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["hide-desktop"] ? structure["hide-desktop"] : "false"}
        label="Hide on desktop"
      />

      {customClassesSetting}
    </React.Fragment>
  );

  const designSettings = (
    <React.Fragment>
      <VariantBuilderSetting
        object={section_column}
        label="Overlay Color"
        setting_name="[options][color]"
        setting_type="color_v2"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure.color ? structure.color : ""}
        gradient={true}
        show_reset={true}
      />

      <VariantBuilderSetting
        object={section_column}
        label="Overlay Opacity"
        setting_name="[options][opacity]"
        setting_type="range"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure.opacity != null ? structure.opacity : null}
        placeholder={0}
        min={0}
        max={1}
        range_step={"0.01"}
        show_reset={true}
      />

      <VariantBuilderSetting
        object={section_column}
        label="Overlay Image"
        setting_name="[options][background]"
        setting_type="image"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure.background ? structure.background : ""}
        show_reset={true}
      />

      <VariantBuilderSetting
        object={section_column}
        label="Overlay Image Position"
        setting_name="[options][background-position]"
        setting_type="dropdown"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["background-position"] || "top-center"}
        options={[
          { value: "top-left", text: "Top left" },
          { value: "top-center", text: "Top center" },
          { value: "top-right", text: "Top right" },
          { value: "center-left", text: "Middle left" },
          { value: "center-center", text: "Middle center" },
          { value: "center-right", text: "Middle right" },
          { value: "bottom-left", text: "Bottom left" },
          { value: "bottom-center", text: "Bottom center" },
          { value: "bottom-right", text: "Bottom right" },
        ]}
      />

      <VariantBuilderSetting
        object={section_column}
        label="Overlay Image Size"
        setting_name="[options][background-size]"
        setting_type="dropdown"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["background-size"] || "cover"}
        options={[
          { value: "auto", text: "Image width" },
          { value: "cover", text: "Cover container" },
          { value: "expand-width", text: "Expand to width" },
          { value: "expand-height", text: "Expand to height" },
        ]}
      />

      <VariantBuilderSetting
        object={section_column}
        label="Column Border Style"
        setting_name="[options][border-style]"
        setting_type="border_style"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["border-style"] ? structure["border-style"] : "none"}
      />

      <VariantBuilderSetting
        object={section_column}
        label="Column Border Color"
        setting_name="[options][border-color]"
        setting_type="color_v2"
        object_type="section_columns"
        object_id={section_column.id}
        value={structure["border-color"] ? structure["border-color"] : "#000"}
        condition={structure["border-style"] !== "none"}
      />

      {structure["border-style"] !== "none" && (
        <div
          className="col-xs-12 some-vertical-space setting"
          style={{ marginBottom: "5px" }}
        >
          <div className="row">
            <div className="col-xs-12">
              <div className="mb-[5px] text-xl text-black dark:text-white">
                Column Border Width
              </div>
              {["left", "top", "bottom", "right"].map((side) => {
                const settingName = `[options][border-width-${side}]`;
                return (
                  <VariantBuilderSetting
                    key={settingName}
                    object={section_column}
                    setting_name={settingName}
                    setting_type="number"
                    object_type="section_columns"
                    object_id={section_column.id}
                    value={
                      structure[`border-width-${side}`] != null
                        ? structure[`border-width-${side}`]
                        : "0"
                    }
                    placeholder="0px"
                    col="3"
                    note={window.capitalizeFirstLetter(side)}
                    classNames="spacing-inputs"
                    min={0}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {customClassesSetting}
    </React.Fragment>
  );

  const parentBackLink = getPanelParentBackLink(
    variant,
    builder.get_object_parent_layer,
    "section_columns",
    section_column.id
  );

  if (props.settings_only) {
    return (
      <React.Fragment>
        <VariantBuilderPanelTitle
          title={`Column ${section_column.position || section_column.id}`}
          object_type="section_columns"
          object_id={section_column.id}
          object={section_column}
          parent_object={parentBackLink}
        />
        <TabGroup
          tabs={tabs}
          tab={tab}
          setActiveTab={(tab) => {
            dispatchCustomEvent("selectObject", {
              object_type: "section_columns",
              object_id: section_column.id,
              tab: tab,
            });
          }}
          className="mb-6"
        >
          {showLayout && <div className="row">{layoutSettings}</div>}
          {showDesign && <div className="row">{designSettings}</div>}
        </TabGroup>
      </React.Fragment>
    );
  }

  return (
    <VariantBuilderSetting
      object={section_column}
      setting_name="[settings_trigger]"
      setting_type="item_v2"
      object_type="section_columns"
      object_id={section_column.id}
      title={`Column ${section_column.position || section_column.id}`}
      label_icon={"fas fa-columns"}
      label_max_length={30}
      label_hover_max_length={18}
      style={{ margin: "0px 0px 5px 0px" }}
      position={section_column.position ? section_column.position : 1}
      duplicate_payload={{
        website_id: section_column.website_id,
        section_id: section_column.section_id,
        options: section_column.options,
      }}
      left_control={() => {
        const hideKey =
          contextData.device === "mobile" ? "hide-mobile" : "hide-desktop";
        const hidden = structure[hideKey] === "true";

        return (
          <button
            title={`${hidden ? "Show" : "Hide"} column on ${
              contextData.device
            }`}
            className="w-[25px] h-[25px] p-0 pt-1 bg-white hover:bg-slate-100 dark:bg-slate-950 dark:hover:bg-slate-800 cursor-pointer border-2 border-solid border-white hover:border-slate-100 dark:border-slate-950 dark:hover:border-slate-800 rounded-lg"
            onClick={(event) => {
              event.stopPropagation();
              builder.update([
                {
                  object_type: "section_columns",
                  object_id: section_column.id,
                  setting_name: `[options][${hideKey}]`,
                  value: hidden ? "false" : "true",
                },
              ]);
            }}
          >
            <FontAwesomeIcon
              icon={`fas fa-eye${hidden ? "-slash" : ""}`}
              className={contextData.dark_mode ? "text-white" : "text-black"}
            />
          </button>
        );
      }}
      onEdit={() => {
        dispatchCustomEvent("selectObject", {
          object_type: "section_columns",
          object_id: section_column.id,
        });
      }}
      hide_duplicate={props.hide_duplicate}
      callbackOnRemove={props.callbackOnRemove}
      callbackOnDuplicate={props.callbackOnDuplicate}
    />
  );
};

export default SectionColumnSettings;
